import { useEffect, useState, useCallback } from "react";
import { Supplier } from "@/types/supplier";
import { supplierService } from "@/services/supplierService";
import { SupplierModal } from "@/components/suppliers/SupplierModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Search, Plus, MoreVertical, Edit2, Trash2, Mail, Phone, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useDebounce } from "@/hooks/useDebounce";
import { TablePageSkeleton } from "@/components/shared/TablePageSkeleton";

export default function Suppliers() {
  const { company } = useAuth();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | undefined>();
  
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState<Supplier | undefined>();

  // Pagination states
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);

  const loadSuppliers = useCallback(async () => {
    if (!company?.slug) return;
    try {
      setLoading(true);
      const response = await supplierService.list({
        slug: company.slug,
        search: debouncedSearch,
        page,
        limit,
      });
      setSuppliers(response.data);
      if (response.pagination) {
        setTotal(response.pagination.total);
      }
    } catch (error: any) {
      toast.error(error.message || "Error al cargar los proveedores");
    } finally {
      setLoading(false);
    }
  }, [company?.slug, debouncedSearch, page, limit]);

  useEffect(() => {
    loadSuppliers();
  }, [loadSuppliers]);

  const handleCreate = () => {
    setSelectedSupplier(undefined);
    setIsModalOpen(true);
  };

  const handleEdit = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (supplier: Supplier) => {
    setSupplierToDelete(supplier);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!supplierToDelete || !company?.slug) return;
    try {
      await supplierService.remove(supplierToDelete.id, { slug: company.slug });
      toast.success("Proveedor eliminado exitosamente");
      loadSuppliers();
    } catch (error: any) {
      toast.error(error.message || "Error al eliminar el proveedor");
    } finally {
      setIsDeleteDialogOpen(false);
      setSupplierToDelete(undefined);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Proveedores</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Gestiona los proveedores y sus cuentas bancarias
          </p>
        </div>
        <Button onClick={handleCreate} className="bg-black hover:bg-gray-800 text-white">
          <Plus className="mr-2 h-4 w-4" /> Crear Proveedor
        </Button>
      </div>

      {loading ? (
        <TablePageSkeleton hasHeader={true} hasMetrics={false} columnCount={6} rowCount={8} />
      ) : (
        <>
          <Card className="p-4 border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900">
            <div className="flex items-center gap-4 mb-4">
              <div className="relative flex-1 max-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Buscar por nombre, NIT o RTU..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                  className="pl-9"
                />
              </div>
            </div>

            <div className="rounded-md border border-slate-200 dark:border-slate-800">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50 dark:bg-slate-800/50">
                    <TableHead>Nombre</TableHead>
                    <TableHead>Identificación</TableHead>
                    <TableHead>Contacto</TableHead>
                    <TableHead>Cuentas</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="w-[80px]">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {suppliers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center h-24 text-slate-500">
                        No se encontraron proveedores.
                      </TableCell>
                    </TableRow>
                  ) : (
                    suppliers.map((supplier) => (
                      <TableRow key={supplier.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50">
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 font-medium text-sm">
                              {supplier.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="font-medium">{supplier.name}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {supplier.nit && <div className="text-slate-600 dark:text-slate-400">NIT: {supplier.nit}</div>}
                            {supplier.rtu_url ? (
                              <div className="text-slate-600 dark:text-slate-400">
                                RTU: <a href={supplier.rtu_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Ver archivo</a>
                              </div>
                            ) : (
                              <div className="text-slate-600 dark:text-slate-400 text-xs italic">Sin RTU</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {supplier.email && (
                              <div className="flex items-center text-xs text-slate-600 dark:text-slate-400">
                                <Mail className="mr-1.5 h-3 w-3" />
                                {supplier.email}
                              </div>
                            )}
                            {supplier.phone && (
                              <div className="flex items-center text-xs text-slate-600 dark:text-slate-400">
                                <Phone className="mr-1.5 h-3 w-3" />
                                {supplier.phone}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-slate-50 dark:bg-slate-800">
                            {supplier.bank_accounts?.length || 0} cuentas
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={supplier.is_active ? "default" : "secondary"}>
                            {supplier.is_active ? "Activo" : "Inactivo"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleEdit(supplier)}>
                                <Edit2 className="mr-2 h-4 w-4" /> Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDeleteClick(supplier)} className="text-red-600">
                                <Trash2 className="mr-2 h-4 w-4" /> Eliminar
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>

          {total > limit && (
            <div className="flex items-center justify-between px-2 py-4">
              <div className="text-sm text-slate-500">
                Mostrando {Math.min((page - 1) * limit + 1, total)} a {Math.min(page * limit, total)} de {total} proveedores
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page * limit >= total}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <SupplierModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={loadSuppliers}
        supplier={selectedSupplier}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Está seguro de eliminar este proveedor?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción desactivará al proveedor "{supplierToDelete?.name}". No podrá ser utilizado en nuevas operaciones, pero se mantendrá en el historial.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar Proveedor
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  
  );
}
